import {
  getUserPushNotificationEnabled,
  updateNotificationPreference,
  updatePushNotificationSetting,
  PREF_KEYS
} from '../../services/notificationPermission';
import notificationSSEService from '../../services/notificationSSE';
import axios from 'axios';
import { url } from '../../client_config/config';

let lastPushResyncAt = 0;
const PUSH_RESYNC_COOLDOWN_MS = 5000;

const state = {
  // Existing push notification state
  isSubscribed: false,
  permission: 'default', // 'default', 'granted', 'denied', 'unsupported'
  token: null,
  showPermissionDialog: false,

  // NEW: In-app notification state
  notifications: [],
  unreadCount: 0,
  isSSEConnected: false,
  isSSEInitialized: false,
  sseError: null
};

const mutations = {
  // Existing mutations
  SET_SUBSCRIBED(state, value) {
    state.isSubscribed = value;
  },
  SET_PERMISSION(state, permission) {
    state.permission = permission;
  },
  SET_TOKEN(state, token) {
    state.token = token;
  },
  SET_SHOW_PERMISSION_DIALOG(state, show) {
    state.showPermissionDialog = show;
  },
  RESET_STATE(state) {
    state.isSubscribed = false;
    state.permission = 'default';
    state.token = null;
    state.showPermissionDialog = false;
  },

  // NEW mutations
  SET_NOTIFICATIONS(state, notifications) {
    state.notifications = notifications;
  },
  ADD_NOTIFICATION(state, notification) {
    state.notifications.unshift(notification);
  },
  MARK_AS_READ(state, notificationId) {
    const notification = state.notifications.find(n => n._id === notificationId);
    if (notification) {
      notification.isRead = true;
    }
  },
  MARK_AS_UNREAD(state, notificationId) {
    const notification = state.notifications.find(n => n._id === notificationId);
    if (notification) {
      notification.isRead = false;
    }
  },
  MARK_ALL_AS_READ(state) {
    state.notifications.forEach(n => n.isRead = true);
  },
  REMOVE_NOTIFICATION(state, notificationId) {
    state.notifications = state.notifications.filter(n => n._id !== notificationId);
  },
  SET_UNREAD_COUNT(state, count) {
    state.unreadCount = count;
  },
  INCREMENT_UNREAD_COUNT(state) {
    state.unreadCount++;
  },
  SET_SSE_CONNECTED(state, isConnected) {
    state.isSSEConnected = isConnected;
  },
  SET_SSE_ERROR(state, error) {
    state.sseError = error;
  },
  SET_SSE_INITIALIZED(state, isInitialized) {
    state.isSSEInitialized = isInitialized;
  }
};

const actions = {
  // Existing actions
  async initializePushNotifications({ commit, rootState }) {
    try {
      const firebaseService = await import('../../services/firebase').catch(() => null);

      if (!firebaseService) {
        commit('SET_PERMISSION', 'unsupported');
        commit('SET_SUBSCRIBED', false);
        commit('SET_TOKEN', null);
        return;
      }

      const { isNotificationSupported, getNotificationPermission } = firebaseService;

      if (!isNotificationSupported()) {
        commit('SET_PERMISSION', 'unsupported');
        commit('SET_SUBSCRIBED', false);
        commit('SET_TOKEN', null);
        return;
      }

      const permission = getNotificationPermission();
      commit('SET_PERMISSION', permission);

      const user = rootState.auth?.user || null;
      const isPushEnabled = getUserPushNotificationEnabled(user);

      // Preference off: do not treat as subscribed (token may still exist locally)
      if (!isPushEnabled) {
        commit('SET_SUBSCRIBED', false);
        commit('SET_TOKEN', null);
        return;
      }

      const { default: pushNotificationService } = await import('../../services/pushNotification');
      // initialize() only returns true after a successful backend token register
      const initialized = await pushNotificationService.initialize();

      if (initialized) {
        commit('SET_SUBSCRIBED', true);
        commit('SET_TOKEN', pushNotificationService.getToken());
      } else {
        commit('SET_SUBSCRIBED', false);
        commit('SET_TOKEN', null);
      }
    } catch (error) {
      console.error('Error initializing push notifications:', error);
      commit('SET_PERMISSION', 'unsupported');
      commit('SET_SUBSCRIBED', false);
      commit('SET_TOKEN', null);
    }
  },

  /**
   * Re-sync FCM token after iOS PWA resume / tab focus.
   * Only runs when permission is already granted and push preference is on.
   */
  async resyncPushToken({ dispatch, rootState }) {
    try {
      const now = Date.now();
      if (now - lastPushResyncAt < PUSH_RESYNC_COOLDOWN_MS) {
        return;
      }

      const firebaseService = await import('../../services/firebase').catch(() => null);
      if (!firebaseService || !firebaseService.isNotificationSupported()) return;

      const permission = firebaseService.getNotificationPermission();
      if (permission !== 'granted') return;

      const user = rootState.auth?.user || null;
      if (!getUserPushNotificationEnabled(user)) return;

      lastPushResyncAt = now;
      await dispatch('initializePushNotifications');
    } catch (error) {
      console.warn('Push token resync failed:', error);
    }
  },

  async subscribeToPushNotifications({ commit, dispatch, rootState }) {
    try {
      const { default: pushNotificationService } = await import('../../services/pushNotification');
      const token = await pushNotificationService.subscribe();
      const userType = rootState.auth?.user?.userType;

      await updatePushNotificationSetting(userType, true);
      await updateNotificationPreference(PREF_KEYS.GRANTED);
      await dispatch('auth/getProfile', null, { root: true });

      commit('SET_SUBSCRIBED', true);
      commit('SET_TOKEN', token);
      commit('SET_PERMISSION', 'granted');
      commit('SET_SHOW_PERMISSION_DIALOG', false);

      dispatch('showSuccess', 'Push notifications enabled', { root: true });
      return token;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      dispatch('showError', error.message || 'Failed to enable push notifications', { root: true });
      throw error;
    }
  },

  async unsubscribeFromPushNotifications({ commit, dispatch, rootState }) {
    try {
      const { default: pushNotificationService } = await import('../../services/pushNotification');
      await pushNotificationService.unsubscribe();
      const userType = rootState.auth?.user?.userType;

      await updatePushNotificationSetting(userType, false);
      await dispatch('auth/getProfile', null, { root: true });

      commit('SET_SUBSCRIBED', false);
      commit('SET_TOKEN', null);

      dispatch('showSuccess', 'Push notifications disabled', { root: true });
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      dispatch('showError', error.message || 'Failed to disable push notifications', { root: true });
      throw error;
    }
  },

  showPermissionDialog({ commit }) {
    commit('SET_SHOW_PERMISSION_DIALOG', true);
  },

  hidePermissionDialog({ commit }) {
    commit('SET_SHOW_PERMISSION_DIALOG', false);
  },

  resetNotifications({ commit }) {
    commit('RESET_STATE');
  },

  // NEW actions
  connectSSE({ commit, state, rootState }) {
    const token = rootState.auth?.token || localStorage.getItem('token');
    if (!token) {
      console.warn('[Vuex] No token available to connect to SSE');
      return;
    }

    console.log('[Vuex] Dispatching connectSSE with token');
    
    if (!state.isSSEInitialized) {
      notificationSSEService.onNotification((notification) => {
        console.log('[Vuex] Received notification from SSE service:', notification.title || notification.type);
        commit('ADD_NOTIFICATION', notification);
        if (!notification.isRead) {
          console.log('[Vuex] Notification is unread, incrementing unread count');
          commit('INCREMENT_UNREAD_COUNT');
        }
      });

      notificationSSEService.onEvent((event) => {
        console.log('[Vuex] SSE Connection event:', event.type);
        if (event.type === 'connected') {
          commit('SET_SSE_CONNECTED', true);
          commit('SET_SSE_ERROR', null);
        } else if (event.type === 'disconnected') {
          commit('SET_SSE_CONNECTED', false);
        } else if (event.type === 'error') {
          commit('SET_SSE_ERROR', event.error);
        }
      });
      
      commit('SET_SSE_INITIALIZED', true);
    }

    notificationSSEService.connect(token);
  },

  disconnectSSE({ commit }) {
    notificationSSEService.disconnect();
    commit('SET_SSE_CONNECTED', false);
  },

  async fetchNotifications({ commit, rootState }) {
    try {
      const token = rootState.auth?.token;
      const response = await axios.get(`${url}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      commit('SET_NOTIFICATIONS', response.data.notifications);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  async markAsRead({ commit, dispatch }, notificationId) {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${url}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      commit('MARK_AS_READ', notificationId);
      dispatch('updateUnreadCount');
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  },

  async markAsUnread({ commit, dispatch }, notificationId) {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${url}/notifications/${notificationId}/unread`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      commit('MARK_AS_UNREAD', notificationId);
      dispatch('updateUnreadCount');
    } catch (error) {
      console.error('Error marking notification as unread:', error);
      throw error;
    }
  },

  async markAllAsRead({ commit, dispatch }) {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${url}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      commit('MARK_ALL_AS_READ');
      dispatch('updateUnreadCount');
    } catch (error) {
      console.error('Error marking all as read:', error);
      throw error;
    }
  },

  async deleteNotification({ commit, dispatch }, notificationId) {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${url}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      commit('REMOVE_NOTIFICATION', notificationId);
      dispatch('updateUnreadCount');
    } catch (error) {
      console.error('Error deleting notification:', error);
      throw error;
    }
  },

  async updateUnreadCount({ commit }) {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${url}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      commit('SET_UNREAD_COUNT', response.data.unreadCount);
    } catch (error) {
      console.error('Error updating unread count:', error);
    }
  }
};

const getters = {
  // Existing getters
  isSubscribed: state => state.isSubscribed,
  permission: state => state.permission,
  token: state => state.token,
  showPermissionDialog: state => state.showPermissionDialog,
  canSubscribe: state => state.permission === 'default' || state.permission === 'granted',

  // NEW getters
  notifications: state => state.notifications,
  unreadNotifications: state => state.notifications.filter(n => !n.isRead),
  unreadCount: state => state.unreadCount,
  isSSEConnected: state => state.isSSEConnected,
  sseError: state => state.sseError,
  recentNotifications: state => state.notifications.slice(0, 5)
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
